<template>
  <main class="pa-15 mb-10">
    <!--  BulletPoints  -->
    <h1 class="text-h4">What to add</h1>
    <v-sheet class="my-5 mb-10 pa-6" elevation="3">
      <p style="font-size: 1.2rem">
        The
        <a
          class="underline-effect"
          href="/communities#governance"
          target="blank"
        >
          FAIRsharing Team</a
        >
        curates and integrates <b>descriptions</b> of community resources such
        as standards and databases, progressively linking both to policies. Our
        curation team keeps track of every record, but resource developers are
        the experts. We encourage you to take ownership of your resources'
        records and help us keep your standard, database or policy description
        up to date. If we already describe your resource,
        <a
          class="underline-effect"
          href="https://fairsharing.gitbook.io/fairsharing/#claiming-a-record"
          target="_blank"
          >claim</a
        >
        and then
        <a
          class="underline-effect"
          href="https://fairsharing.gitbook.io/fairsharing/record-sections-and-fields/how-to-update-a-record"
          target="_blank"
          >update</a
        >
        it as necessary. If your resource is missing, please keep reading to
        start a new record submission.
      </p>
    </v-sheet>
    <div v-if="newData.firstBlock" class="mt-2">
      <v-container>
        <v-row class="block-category">
          <v-col
            v-for="(item, index) in newData.firstBlock.items"
            :key="item + '_' + index"
            cols="12"
            lg="3"
            md="4"
            sm="12"
          >
            <v-card
              class="mx-auto block-category__card"
              height="100%"
              max-width="350"
            >
              <div
                class="text-white d-flex flex-column justify-center block-category__card__gradiant"
              >
                <div class="d-flex justify-center" style="height: 136px">
                  <v-img
                    :src="customIcons.values[item.icon].icon"
                    class="mt-5 contain"
                    height="100px"
                  />
                </div>
                <v-card-title
                  class="d-inline text-h4 text-center text-md-h5 text-lg-h4"
                >
                  {{ newData.firstBlock.items[index].title }}
                </v-card-title>
              </div>
              <v-card-text class="text--primary height-100">
                <span
                  v-safe-html="newData.firstBlock.items[index].description"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-sheet class="my-5 mb-10 pa-6" color="accent3 text-white" elevation="3">
        <div style="background-color: white; width: 100px; padding: 5px">
          <v-img src="/assets/icons/nodatasets.png" width="100" />
        </div>
        <div class="text-center">
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('de')">
            DE
          </v-chip>
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('es')">
            ES
          </v-chip>
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('fr')">
            FR
          </v-chip>
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('it')">
            IT
          </v-chip>
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('jp')">
            JP
          </v-chip>
          <v-chip class="bg-white ma-2" variant="flat" @click="translate('zh')">
            ZH
          </v-chip>
        </div>
        <h2 class="text-h4 my-2">No datasets, please</h2>
        <ul class="circles">
          <li>
            <span>
              <strong>
                No files (excel, doc, csv, sql…), no data relating to an article
                publication:</strong
              >
            </span>
            <ul class="ticks">
              <li>
                The resource should be an organised collection of data and
                datasets rather than just an individual dataset.
              </li>
              <li>
                The resource is findable. Users can access the database via an
                active website and can also browse and/or search the database.
                In contrast, datasets are generally downloadable but not
                searchable, and therefore are not appropriate for FAIRsharing.
              </li>
              <li>
                The resource is accessible. Irrespective of licence type, the
                resource is available to users via a dedicated website (even if
                a log in or payment is required).
              </li>
            </ul>
          </li>
          <li>
            <span>
              <strong>Where should I store my dataset?</strong>
            </span>
            <ul class="arrows">
              <li>
                <span>
                  If you would like to find a home for your data, try browsing
                  our complete
                  <a
                    class="underline-effect font-weight-black text-white"
                    href="/browse/subject"
                    >subject hierarchy</a
                  >
                  or searching our
                  <a
                    class="underline-effect font-weight-black text-white"
                    href="/databases"
                    >database records</a
                  >
                  to find an appropriate database for your data type.
                </span>
              </li>
              <li>
                <span>
                  Alternatively, you could look at our collection of
                  <a
                    class="underline-effect font-weight-black text-white"
                    href="/GeneralRepositoryComparison"
                    >Generalist Repositories</a
                  >
                  or browse all
                  <a
                    class="underline-effect font-weight-black text-white"
                    href="/browse/subject?term=Subject%2520Agnostic"
                    >subject-agnostic records</a
                  >, which may provide you with a location to store data from a
                  wider range of research areas. If you are submitting to a
                  particular journal, you can also search for that journal or
                  its publisher; if they are registered with us, then their
                  <a
                    class="underline-effect font-weight-black text-white"
                    href="/search?fairsharingRegistry=Policy"
                    >FAIRsharing data policy record(s)</a
                  >
                  may contain a list of recommended databases and/or standards.
                </span>
              </li>
            </ul>
          </li>
        </ul>
      </v-sheet>
      <v-sheet
        :class="['mb-5 pa-6', { 'mx-25-percent': $vuetify.display.mdAndUp }]"
        color="uncertain_color text-white"
        elevation="3"
      >
        <h3 class="text-h4">Get Started!</h3>
        <p class="text-left" style="font-size: 1.2rem">
          If you have not already done so, you may wish to
          <a
            class="underline-effect font-weight-black text-white"
            href="https://fairsharing.gitbook.io/fairsharing/"
            target="_blank"
            >create an account.</a
          >
        </p>
        <p class="text-left" style="font-size: 1.2rem">
          Any questions? Don't hesitate to
          <a
            class="font-weight-black text-white underline-effect"
            href="mailto:contact@fairsharing.org"
            >contact us</a
          >
          or to
          <a
            class="font-weight-black text-white underline-effect"
            href="https://fairsharing.gitbook.io/fairsharing/"
          >
            consult our documentation.
          </a>
        </p>
        <v-btn color="primary" to="/create"> Add New Record </v-btn>
      </v-sheet>
    </div>

    <v-layout justify-center row>
      <v-dialog v-model="showTranslation" max-width="700px">
        <v-card>
          <v-card-title>
            <!-- nothing to see here -->
          </v-card-title>
          <v-card-text>
            {{ translatedText }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="blue-darken-1"
              variant="elevated"
              @click="showTranslation = false"
            >
              {{ closeButton }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </main>
</template>

<script>
import newData from "@/data/NewStaticPageData.json";
import noDatasetsPlease from "@/data/noDatasetsPlease.json";
import customIcons from "@/plugins/icons";

export default {
  name: "New",
  data: () => {
    return {
      newData,
      showTranslation: false,
      translatedText: "",
      closeButton: "",
      customIcons: customIcons,
    };
  },
  methods: {
    translate(language) {
      this.translatedText = noDatasetsPlease[language].text;
      this.closeButton = noDatasetsPlease[language].close;
      this.showTranslation = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.block-category {
  &__card {
    transition: all 0.2ms ease;
    -webkit-transition: all 0.2s ease;
    -moz-transition: all 0.2s ease;
    -o-transition: all 0.2s ease;
    box-shadow: 0 1rem 2rem rgba(black, 0.15) !important;
    -webkit-box-shadow: 0 1rem 2rem rgba(black, 0.15) !important;
    -moz-box-shadow: 0 1rem 2rem rgba(black, 0.15) !important;
    -o-box-shadow: 0 1rem 2rem rgba(black, 0.15) !important;

    &:hover {
      transform: scale(1.05);
      -moz-transform: scale(1.05);
      -webkit-transform: scale(1.05);
      -o-transform: scale(1.05);
    }

    &__gradiant {
      height: 200px;
      background: rgb(171, 171, 171);
    }
  }
}
.v-divider {
  margin: 8px;
}
.circles {
  list-style-type: "\2022";
}
.arrows {
  list-style-type: "\27A4";
}
.ticks {
  list-style-type: "\2713\0020";
}
li span {
  position: relative;
  left: 6px;
}
</style>
